import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import type {
  ColumnAnalyzeRequest,
  ColumnAnalyzeResponse,
  InteractionSurfaceRequest,
  InteractionSurfaceResponse,
} from './types';

function useDebounced<T>(value: T, ms = 200): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), ms);
    return () => window.clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

export function useColumnAnalysis(request: ColumnAnalyzeRequest) {
  const debounced = useDebounced(request, 200);
  return useQuery<ColumnAnalyzeResponse>({
    queryKey: ['columns', 'analyze', debounced],
    queryFn: async () => {
      const { data } = await api.post<ColumnAnalyzeResponse>(
        '/api/columns/analyze',
        debounced,
      );
      return data;
    },
    placeholderData: (prev) => prev,
    retry: 0,
  });
}

export function useInteractionSurface(
  request: InteractionSurfaceRequest,
  enabled: boolean,
) {
  const debounced = useDebounced(request, 350);
  return useQuery<InteractionSurfaceResponse>({
    queryKey: ['columns', 'surface', debounced],
    queryFn: async () => {
      const { data } = await api.post<InteractionSurfaceResponse>(
        '/api/columns/interaction-surface',
        debounced,
      );
      return data;
    },
    enabled,
    placeholderData: (prev) => prev,
    retry: 0,
    staleTime: 60_000,
  });
}
